import React, { useMemo, useState } from 'react';
import { AlertCircle, PackageOpen, ShieldCheck, Truck } from 'lucide-react';
import { Alert, Button, Icon, PageHeader, SectionHeader, SegmentedTabs, SegmentedTabItem, StatCard } from '@/components/common';
import ProductCaseCard from '@/components/customer-dashboard/ProductCaseCard';
import { useAuth } from '@/context/AuthContext';
import { useCustomerProducts } from '@/hooks/useCustomerProducts';
import { useFileClaim } from '@/hooks/useFileClaim';
import type { ProductState } from '@/types/product';

type Filter = 'all' | ProductState;

const CardSkeleton: React.FC = () => (
  <div aria-hidden className="tw-flex tw-animate-pulse tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
    <div className="tw-flex tw-items-center tw-gap-[14px]">
      <span className="tw-h-14 tw-w-14 tw-rounded-xl tw-bg-ds-subtle" />
      <span className="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
        <span className="tw-h-4 tw-w-2/3 tw-rounded tw-bg-ds-subtle" />
        <span className="tw-h-3 tw-w-1/2 tw-rounded tw-bg-ds-subtle" />
      </span>
    </div>
    <span className="tw-h-24 tw-rounded-xl tw-bg-ds-subtle" />
    <span className="tw-h-9 tw-rounded-[10px] tw-bg-ds-subtle" />
  </div>
);

/** T1 My Plans: every purchased product and its protection status. */
const MyPlans: React.FC = () => {
  const { user } = useAuth();
  const { products, loading, error, reload } = useCustomerProducts();
  const { fileClaim, busyPolicyId } = useFileClaim();
  const [filter, setFilter] = useState<Filter>('all');

  const counts = useMemo(
    () => ({
      all: products.length,
      protected: products.filter((p) => p.state === 'protected').length,
      not_protected: products.filter((p) => p.state === 'not_protected').length,
      on_the_way: products.filter((p) => p.state === 'on_the_way').length,
    }),
    [products],
  );
  const visible = filter === 'all' ? products : products.filter((p) => p.state === filter);

  const tabs: SegmentedTabItem<Filter>[] = [
    { value: 'all', label: `All · ${counts.all}`, shortLabel: 'All' },
    { value: 'protected', label: `Protected · ${counts.protected}`, shortLabel: 'Protected' },
    { value: 'not_protected', label: `Not protected · ${counts.not_protected}`, shortLabel: 'Unprotected' },
    { value: 'on_the_way', label: `On the way · ${counts.on_the_way}`, shortLabel: 'Arriving' },
  ];

  const firstName = user?.name?.trim().split(/\s+/)[0];

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader title={firstName ? `Hi ${firstName}` : 'Hi there'} subtitle="Your products and their protection, in one place." />

      <section aria-label="Summary" className="tw-flex tw-gap-2 md:tw-gap-4">
        <StatCard icon={ShieldCheck} tone="brand" value={loading ? '–' : counts.protected} label="Protected" />
        <StatCard icon={AlertCircle} tone="warning" value={loading ? '–' : counts.not_protected} label="Not protected" />
        <StatCard icon={Truck} tone="info" value={loading ? '–' : counts.on_the_way} label="On the way" />
      </section>

      <section aria-labelledby="products-heading" className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center">
          <SectionHeader title="Your products" className="md:tw-flex-1" />
          <SegmentedTabs items={tabs} value={filter} onChange={setFilter} label="Filter products" />
        </div>

        {error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : loading ? (
          <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 lg:tw-gap-5" aria-busy>
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : visible.length === 0 ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-dashed tw-border-ds-border-strong tw-px-6 tw-py-12 tw-text-center">
            <Icon icon={PackageOpen} size={28} className="tw-text-ds-text-tertiary" />
            <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
              {filter === 'all' ? 'No products yet' : 'Nothing here'}
            </p>
            <p className="tw-max-w-sm tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
              {filter === 'all'
                ? 'Products you buy from brands that offer Protega protection show up here automatically.'
                : 'No products match this filter.'}
            </p>
          </div>
        ) : (
          <div className="tw-grid tw-items-start tw-gap-4 md:tw-grid-cols-2 lg:tw-gap-5">
            {visible.map((product) => (
              <ProductCaseCard
                key={product.id}
                product={product}
                onFileClaim={fileClaim}
                filingClaim={busyPolicyId === product.plan?.policy.id}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default MyPlans;
